<template>
    <div ref="rootRef" class="searchable-select-wrapper" :class="attrs.class" :style="rootStyle">
        <v-menu :model-value="menu" :close-on-content-click="false" :eager="true" :content-class="menuContentClass"
            location="bottom start" origin="top start" :offset="2" transition="none" @update:model-value="onMenuUpdate">
            <template #activator="{ props: activatorProps }">
                <div class="searchable-select__activator">
                    <v-text-field v-bind="{ ...getSafeActivatorProps(activatorProps), ...getTextFieldAttrs() }"
                        :model-value="selectedTitle" :variant="variant" :density="density" :hide-details="hideDetails"
                        readonly class="searchable-select" append-inner-icon="mdi-menu-down"
                        @mousedown.prevent.stop="toggleMenu" @click.prevent.stop @keydown="onActivatorKeydown" />
                </div>
            </template>

            <v-card class="searchable-select__menu-card" :style="menuStyle">
                <div class="pa-2 border-b" :data-searchable-select-id="componentId" @mousedown.stop @click.stop
                    @keydown.capture="onSearchKeydown">
                    <v-text-field :key="searchInputKey" v-focus-input="menu" :model-value="searchText" density="compact"
                        :placeholder="searchPlaceholder" prepend-inner-icon="mdi-magnify" variant="outlined" clearable
                        hide-details :error="!!searchText && filteredItems.length === 0"
                        @update:model-value="onSearchTextUpdate" @click:clear="clearSearchText" @mousedown.stop
                        @click.stop />
                </div>

                <v-list class="searchable-select__list pa-0" :density="density">
                    <v-list-item v-for="(item, index) in filteredItems" :key="getItemKey(item, index)"
                        :data-searchable-select-option-id="componentId" :data-searchable-select-option-index="index"
                        :title="getRawItemTitle(item)" :active="index === activeIndex" :density="density"
                        :class="getItemClasses(item, index)" class="searchable-select__list-item"
                        @mouseenter="activeIndex = index" @mousedown.prevent.stop
                        @click.prevent.stop="selectItem(item)" />

                    <v-list-item v-if="filteredItems.length === 0" :title="noDataText" :density="density"
                        class="searchable-select__list-item" disabled />
                </v-list>
            </v-card>
        </v-menu>
    </div>
</template>

<script setup lang="ts">
import {
    computed,
    getCurrentInstance,
    nextTick,
    ref,
    useAttrs,
    watch,
    type Directive,
} from 'vue'

defineOptions({
    inheritAttrs: false,
})

type SelectValue = string | number | null

type RawItem = Record<string, unknown>

const attrs = useAttrs()

const props = withDefaults(
    defineProps<{
        modelValue: SelectValue
        items: RawItem[]
        itemTitle?: string
        itemValue?: string
        searchPlaceholder?: string
        noDataText?: string
        variant?: 'outlined' | 'filled' | 'underlined' | 'plain' | 'solo' | 'solo-inverted' | 'solo-filled'
        density?: 'default' | 'comfortable' | 'compact'
        hideDetails?: boolean | 'auto'
    }>(),
    {
        itemTitle: 'title',
        itemValue: 'value',
        searchPlaceholder: '',
        noDataText: 'データがありません',
        variant: 'outlined',
        density: 'compact',
        hideDetails: 'auto',
    },
)

const emit = defineEmits<{
    'update:modelValue': [value: SelectValue]
    change: [value: SelectValue]
}>()

const instance = getCurrentInstance()
const componentId = `searchable-select-${instance?.uid ?? Math.random().toString(36).slice(2)}`

const menu = ref(false)
const searchText = ref('')
const activeIndex = ref(0)
const searchInputKey = ref(0)
const rootRef = ref<HTMLElement | null>(null)
const selectFontSize = ref('')
let scrollToken = 0

const menuContentClass = `searchable-select-menu-content ${componentId}-menu-content`

const rootStyle = computed(() => {
    const base = attrs.style as any
    const fontStyle = selectFontSize.value
        ? { '--searchable-select-font-size': selectFontSize.value }
        : undefined

    if (base == null) {
        return fontStyle
    }

    if (Array.isArray(base)) {
        return [...base, fontStyle].filter(Boolean)
    }

    return [base, fontStyle].filter(Boolean)
})

const menuStyle = computed(() => {
    if (!selectFontSize.value) {
        return undefined
    }

    return {
        '--searchable-select-font-size': selectFontSize.value,
    }
})

const filteredItems = computed<RawItem[]>(() => {
    const keyword = normalize(searchText.value)

    if (!keyword) {
        return props.items
    }

    return props.items.filter((item) => {
        return normalize(getRawItemTitle(item)).includes(keyword)
    })
})

const selectedTitle = computed(() => {
    const selectedItem = props.items.find((item) => {
        return isSameSelectValue(getRawItemValue(item), props.modelValue)
    })

    if (!selectedItem) {
        return ''
    }

    return getRawItemTitle(selectedItem)
})

watch(
    searchText,
    async (newValue, oldValue) => {
        const currentToken = ++scrollToken
        const isDeleting = newValue.length < oldValue.length

        activeIndex.value = getDefaultActiveIndexInFilteredItems()

        await waitListUpdated()

        if (currentToken !== scrollToken) {
            return
        }

        if (isDeleting) {
            resetMenuScrollTop()
        }

        if (activeIndex.value >= 0) {
            scrollActiveItemIntoView('nearest', currentToken)
        }
    },
    {
        flush: 'post',
    },
)

watch(
    () => props.items,
    () => {
        syncActiveIndexWithSelectedItem()
    },
)

watch(
    () => props.modelValue,
    () => {
        syncActiveIndexWithSelectedItem()
    },
)

const vFocusInput: Directive<HTMLElement, boolean> = {
    mounted(el, binding) {
        if (binding.value) {
            focusInputElement(el)
        }
    },
    updated(el, binding) {
        if (binding.value) {
            focusInputElement(el)
        }
    },
}

function getTextFieldAttrs(): Record<string, unknown> {
    const {
        class: _class,
        style: _style,
        ...restAttrs
    } = attrs

    return restAttrs
}

function openMenu() {
    if (menu.value) {
        focusSearchInput()
        return
    }

    onMenuUpdate(true)
}

function toggleMenu() {
    if (menu.value) {
        closeMenu()
        return
    }

    onMenuUpdate(true)
}

function onMenuUpdate(open: boolean) {
    menu.value = open

    if (!open) {
        resetSearchState()
        return
    }

    syncSelectFontSize()
    syncActiveIndexWithSelectedItem()
    searchInputKey.value += 1

    const currentToken = ++scrollToken

    nextTick(() => {
        syncSelectFontSize()
        focusSearchInput()
        scrollActiveItemIntoView('nearest', currentToken)
    })
}

function onActivatorKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown') {
        event.preventDefault()

        if (!menu.value) {
            openMenu()
            return
        }

        moveActive(1)
        return
    }

    if (event.key === 'ArrowUp') {
        event.preventDefault()

        if (!menu.value) {
            openMenu()
            return
        }

        moveActive(-1)
        return
    }

    if (event.key === 'Enter') {
        event.preventDefault()

        if (!menu.value) {
            openMenu()
            return
        }

        selectActiveItem()
        return
    }

    if (event.key === 'Escape') {
        event.preventDefault()
        closeMenu()
    }
}

function onSearchKeydown(event: KeyboardEvent) {
    event.stopPropagation()

    if (event.key === 'ArrowDown') {
        event.preventDefault()
        moveActive(1)
        return
    }

    if (event.key === 'ArrowUp') {
        event.preventDefault()
        moveActive(-1)
        return
    }

    if (event.key === 'Enter') {
        event.preventDefault()
        selectActiveItem()
        return
    }

    if (event.key === 'Escape') {
        event.preventDefault()
        closeMenu()
    }
}

function onSearchTextUpdate(value: string | null) {
    searchText.value = value ?? ''
}

function clearSearchText(event?: MouseEvent) {
    event?.stopPropagation()

    searchText.value = ''
    syncActiveIndexWithSelectedItem()

    const currentToken = ++scrollToken

    nextTick(() => {
        focusSearchInput()
        scrollActiveItemIntoView('nearest', currentToken)
    })
}

function closeMenu() {
    menu.value = false
    resetSearchState()
}

function resetSearchState() {
    searchText.value = ''
    syncActiveIndexWithSelectedItem()
}

function getSelectedIndexInFilteredItems(): number {
    return filteredItems.value.findIndex((item) => {
        return isSameSelectValue(getRawItemValue(item), props.modelValue)
    })
}

function getDefaultActiveIndexInFilteredItems(): number {
    const selectedIndex = getSelectedIndexInFilteredItems()

    if (selectedIndex >= 0) {
        return selectedIndex
    }

    return filteredItems.value.length > 0 ? 0 : -1
}

function syncActiveIndexWithSelectedItem() {
    activeIndex.value = getDefaultActiveIndexInFilteredItems()
}

function moveActive(direction: number) {
    const total = filteredItems.value.length

    if (!total) {
        activeIndex.value = -1
        return
    }

    const previousIndex = activeIndex.value

    let nextIndex: number

    if (previousIndex < 0) {
        nextIndex = direction > 0 ? 0 : total - 1
    } else {
        nextIndex = previousIndex + direction

        if (nextIndex >= total) {
            nextIndex = 0
        }

        if (nextIndex < 0) {
            nextIndex = total - 1
        }
    }

    activeIndex.value = nextIndex

    const currentToken = ++scrollToken

    const isDownWrap = previousIndex === total - 1 && nextIndex === 0
    const isUpWrap = previousIndex === 0 && nextIndex === total - 1

    if (isDownWrap) {
        scrollActiveItemIntoView('top', currentToken)
        return
    }

    if (isUpWrap) {
        scrollActiveItemIntoView('bottom', currentToken)
        return
    }

    scrollActiveItemIntoView('nearest', currentToken)
}

function selectActiveItem() {
    if (activeIndex.value < 0) {
        return
    }

    const rawItem = filteredItems.value[activeIndex.value]

    if (!rawItem) {
        return
    }

    commitValue(getRawItemValue(rawItem))
}

function selectItem(item: RawItem) {
    commitValue(getRawItemValue(item))
}

function commitValue(value: SelectValue) {
    emit('update:modelValue', value)
    emit('change', value)

    closeMenu()
}

function getItemClasses(item: RawItem, index: number): Record<string, boolean> {
    const selected = isSelectedItem(item)
    const active = index === activeIndex.value

    return {
        'searchable-select__item--active': active,
        'searchable-select__item--selected': selected,
        'searchable-select__item--active-selected': active && selected,
    }
}

function isSelectedItem(item: RawItem): boolean {
    return isSameSelectValue(getRawItemValue(item), props.modelValue)
}

function isSameSelectValue(left: SelectValue, right: SelectValue): boolean {
    if (left === null || right === null) {
        return left === right
    }

    return String(left) === String(right)
}

function getRawItemTitle(item: RawItem): string {
    const title = item[props.itemTitle]

    if (typeof title === 'string' || typeof title === 'number') {
        return String(title)
    }

    return ''
}

function getRawItemValue(item: RawItem): SelectValue {
    const value = item[props.itemValue]

    if (typeof value === 'string' || typeof value === 'number' || value === null) {
        return value
    }

    return null
}

function getItemKey(item: RawItem, index: number): string {
    const value = getRawItemValue(item)

    if (value !== null) {
        return String(value)
    }

    return String(index)
}

function normalize(value: string | null | undefined): string {
    return String(value ?? '').trim().toLowerCase()
}

type ScrollMode = 'top' | 'bottom' | 'nearest'

async function scrollActiveItemIntoView(
    mode: ScrollMode = 'nearest',
    token = scrollToken,
) {
    await waitListUpdated()

    if (token !== scrollToken) {
        return
    }

    const activeItem = document.querySelector(
        `[data-searchable-select-option-id="${componentId}"][data-searchable-select-option-index="${activeIndex.value}"]`,
    ) as HTMLElement | null

    if (!activeItem) {
        return
    }

    const scrollContainer = findScrollContainer(activeItem)

    if (!scrollContainer) {
        return
    }

    if (mode === 'top') {
        scrollContainer.scrollTop = 0
        return
    }

    if (mode === 'bottom') {
        scrollContainer.scrollTop =
            scrollContainer.scrollHeight - scrollContainer.clientHeight
        return
    }

    scrollItemNearest(activeItem, scrollContainer)
}

function resetMenuScrollTop() {
    const firstItem = document.querySelector(
        `[data-searchable-select-option-id="${componentId}"][data-searchable-select-option-index="0"]`,
    ) as HTMLElement | null

    if (!firstItem) {
        return
    }

    const scrollContainer = findScrollContainer(firstItem)

    if (scrollContainer) {
        scrollContainer.scrollTop = 0
    }
}

function focusSearchInput() {
    const wrapper = document.querySelector(
        `[data-searchable-select-id="${componentId}"]`,
    ) as HTMLElement | null

    if (!wrapper) {
        return
    }

    focusInputElement(wrapper)
}

function focusInputElement(root: HTMLElement) {
    const run = () => {
        const input = root.querySelector('input') as HTMLInputElement | null

        input?.focus()
    }

    nextTick(() => {
        requestAnimationFrame(run)
        window.setTimeout(run, 50)
    })
}

function scrollItemNearest(item: HTMLElement, scrollContainer: HTMLElement) {
    const itemRect = item.getBoundingClientRect()
    const containerRect = scrollContainer.getBoundingClientRect()

    if (itemRect.top < containerRect.top) {
        scrollContainer.scrollTop -= containerRect.top - itemRect.top
        return
    }

    if (itemRect.bottom > containerRect.bottom) {
        scrollContainer.scrollTop += itemRect.bottom - containerRect.bottom
    }
}

function findScrollContainer(element: HTMLElement): HTMLElement | null {
    let current: HTMLElement | null = element.parentElement

    while (current) {
        const style = window.getComputedStyle(current)

        const canScroll =
            ['auto', 'scroll', 'overlay'].includes(style.overflowY) &&
            current.scrollHeight > current.clientHeight

        if (canScroll) {
            return current
        }

        current = current.parentElement
    }

    return null
}

function nextFrame(): Promise<void> {
    return new Promise((resolve) => {
        requestAnimationFrame(() => resolve())
    })
}

function syncSelectFontSize() {
    const rootElement = rootRef.value

    if (!rootElement) {
        return
    }

    const inputElement = rootElement.querySelector(
        '.v-field__input input, .v-field__input, input',
    ) as HTMLElement | null

    const targetElement = inputElement ?? rootElement
    const fontSize = window.getComputedStyle(targetElement).fontSize

    if (fontSize) {
        selectFontSize.value = fontSize
    }
}

function getSafeActivatorProps(
    activatorProps: Record<string, unknown>,
): Record<string, unknown> {
    const safeProps = { ...activatorProps }

    delete safeProps.onClick
    delete safeProps.onMousedown
    delete safeProps.onKeydown

    return safeProps
}

async function waitListUpdated() {
    await nextTick()
    await nextTick()
    await nextFrame()
    await nextFrame()
}
</script>

<style scoped>
.searchable-select-wrapper {
    font-size: var(--searchable-select-font-size, inherit);
}

.searchable-select {
    font-size: var(--searchable-select-font-size, inherit);
}

.searchable-select :deep(.v-field__input),
.searchable-select :deep(.v-field__input input),
.searchable-select :deep(input) {
    font-size: var(--searchable-select-font-size, inherit);
}

.searchable-select__menu-card {
    font-size: var(--searchable-select-font-size, inherit);
    overflow: hidden;
}

.searchable-select__list-item :deep(.v-list-item-title) {
    font-size: var(--searchable-select-font-size, inherit);
}

.searchable-select__list {
    /* padding-bottom: 0.25rem !important; */
    max-height: 260px;
    overflow-y: auto;
}

.searchable-select__list-item {
    cursor: pointer;
}

:global(.searchable-select-menu-content .v-list-item.searchable-select__item--active) {
    background-color: rgba(var(--v-theme-on-surface), 0.10) !important;
}

:global(.searchable-select-menu-content .v-list-item.searchable-select__item--selected) {
    background-color: rgba(var(--v-theme-on-surface), 0.16) !important;
}

:global(.searchable-select-menu-content .v-list-item.searchable-select__item--active-selected) {
    background-color: rgba(var(--v-theme-on-surface), 0.22) !important;
}

:global(.searchable-select-menu-content .v-list-item.searchable-select__item--selected .v-list-item-title) {
    font-weight: 600;
}
</style>